import axios from '@api';
import { normalizeTopicIds } from '@helpers';
import { transformObjectToSnakeCase } from '@utilities';

import type {
  PinnedAnswer,
  PinnedAnswersResponse,
  PromptOption,
  SearchAnswers,
  SearchPrompts,
} from './types';

interface ReadSearchResultsOptions {
  attributeIds: Array<null | number | string | undefined>;
  documentId?: string;
  entryId?: string;
  narrativeId?: string;
  promptId: string;
  searchAnswers?: SearchAnswers;
  searchPrompts?: SearchPrompts;
  searchQuery?: string;
  serviceName?: string;
  signal?: AbortSignal;
  topicIds?: Array<null | number | string | undefined>;
}

/**
 * Create a pinned answer.
 */
export const createPinnedAnswer = ({
  documentId,
  option,
  promptId,
}: {
  documentId: string;
  option: PromptOption;
  promptId: string;
}) => {
  return axios<PinnedAnswer>({
    data: { document_id: documentId, ...option, prompt_id: promptId },
    method: 'post',
    url: '/v2/api/pinned-answers',
    withCredentials: true,
  });
};

/**
 * Read prompt search results.
 */
export const readSearchResults = ({
  attributeIds,
  documentId,
  entryId,
  narrativeId,
  promptId,
  searchAnswers = {},
  searchPrompts = [],
  searchQuery,
  serviceName = '',
  signal,
  topicIds = [],
}: ReadSearchResultsOptions) => {
  const answers: Record<string, never> | string = searchPrompts.flat().includes(promptId)
    ? JSON.stringify(searchAnswers)
    : {};
  const normalizedTopicIds = normalizeTopicIds(topicIds);
  const isDynamicNarrative = narrativeId === 'dynamic';

  return axios<PromptOption[]>({
    method: 'get',
    params: {
      answers,
      attributeId: attributeIds.join(','),
      documentId,
      narrativeId,
      parent_id: entryId,
      search: searchQuery,
      serviceName: serviceName || undefined,
      ...(isDynamicNarrative && normalizedTopicIds.length
        ? { topicId: normalizedTopicIds.join(',') }
        : {}),
    },
    signal,
    url: `/api/prompts/${promptId}/search`,
    withCredentials: true,
  });
};

/**
 * Read pinned answers.
 */
export const readPinnedAnswers = ({
  documentId,
  signal,
}: {
  documentId: string;
  signal?: AbortSignal;
}) => {
  return axios<PinnedAnswersResponse>({
    method: 'get',
    params: transformObjectToSnakeCase({ documentId }),
    signal,
    url: '/v2/api/pinned-answers/',
    withCredentials: true,
  });
};

/**
 * Delete a pinned answer.
 */
export const deletePinnedAnswer = ({ pinnedAnswerId }: { pinnedAnswerId: string }) => {
  return axios<void>({
    method: 'delete',
    url: `/v2/api/pinned-answers/${pinnedAnswerId}`,
    withCredentials: true,
  });
};
